import { useFocusRef } from 'hooks/useFocusRef';
import { FC, FocusEvent, KeyboardEvent, PropsWithChildren, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import './tab-name-editor.scss';

export type TabRenameProps = {
  name: string | undefined;
  onTabRenamed: (value: string) => void;
  onCancelRenaming: () => void;
};

export function TabNameEditorHint() {
  return (
    <div className="ms-2">
      <FormattedMessage
        id="tabs.rename.hint"
        values={{
          key: k => <kbd>{k}</kbd>,
          comment: c => <span className="ms-1">{c}</span>,
          hint: h => <small className="comma-separator">{h}</small>,
        }}
      />
    </div>
  );
}

export const TabNameEditor: FC<PropsWithChildren<TabRenameProps>> = ({
  name,
  onTabRenamed,
  onCancelRenaming,
  children,
}) => {
  const inputRef = useFocusRef(true);
  const onRenameBlur = useCallback((e: FocusEvent<HTMLInputElement>) => onTabRenamed(e.target.value), [onTabRenamed]);
  const onRenameKeyPress = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      switch (e.key) {
        case 'Enter':
        case 'Tab':
          const input = e.target as HTMLInputElement;
          onTabRenamed(input.value);
          break;
        case 'Escape':
          onCancelRenaming();
          break;
      }
    },
    [onTabRenamed, onCancelRenaming],
  );

  const { formatMessage } = useIntl();
  const placeholder = formatMessage({ id: 'tabs.rename.placeholder' });

  return (
    <div className="d-flex align-items-center">
      <input
        defaultValue={name}
        className="form-control min-width-150px"
        ref={inputRef}
        max={20}
        onBlur={onRenameBlur}
        onKeyDown={onRenameKeyPress}
        type="text"
        data-e2e="tab-input"
        placeholder={placeholder}
      />
      {children}
    </div>
  );
};
